import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';

import { gray100, primaryButtonBackgroundColor } from '../../../../constants/colors';
import { EMAIL_INPUT_PLACEHOLDER } from '../../../../constants/placeholders';
import { companyInviteSchema } from '../../../../validation/company';
import type { CompanyInviteFormType } from '../../../../types/company';

import CustomTextfield from '../../../uiParts/form/CustomTextfield';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import SideBar from '../../../uiParts/menu/SideBar';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../api/firebase/config';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import { AuthContext } from '../../../../contexts/auth';
import { useAuthRedirect } from '../../../../hooks/useAuthRedirect';
import MetaTitle from '../../../utils/MetaTitle';
import { isOperatorStaff } from '../../../../utils/permission';
import { useProxyAccessControl } from '../../../../hooks/useProxyAccessControl';
import { usePageLayout } from '../../../../hooks/usePageLayout';

const styles = {
  title: {
    '@media screen and (max-width:960px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
  button: {
    width: '400px',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    '@media screen and (max-width: 960px)': {
      width: '-webkit-fill-available',
    },
  },
  formCard: {
    backgroundColor: 'white',
    borderRadius: '4px',
    border: '1px solid #E0E0E0',
    flexDirection: 'column',
    p: '39px',
    '@media screen and (max-width: 960px)': {
      width: 'calc(100% - 32px)',
      maxWidth: '480px',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
      margin: '0 16px',
    },
  },
};

const CompanyInvite: React.FC = (): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const { user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const [loading, setLoading] = useState<boolean>(false);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 代理モード中はアクセス禁止
  useProxyAccessControl();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyInviteFormType>({
    resolver: yupResolver(companyInviteSchema),
    defaultValues: {
      companyName: '',
      userName: '',
      email: '',
    },
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('companyName') === '' ||
        watch('userName') === '' ||
        watch('email') === '' ||
        Boolean(errors.companyName) ||
        Boolean(errors.userName) ||
        Boolean(errors.email)
      );
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const companyName = watch('companyName');
      const userName = watch('userName');
      const email = watch('email');

      const sendCompanyInvitation = httpsCallable<
        { companyName: string; userName: string; email: string },
        { success: boolean; message: string }
      >(functions, 'sendCompanyInvitation');
      const result = await sendCompanyInvitation({ companyName, userName, email });

      if (result.data.success) {
        navigate('complete', { state: { fromCompanyInvite: true } });
      } else {
        setErrorText('メール送信に失敗しました。');
        setOpen(true);
      }
    } catch (error: unknown) {
      let errorMessage = 'メール送信中にエラーが発生しました。';

      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };
        if (firebaseError.code === 'functions/already-exists') {
          errorMessage = 'このメールアドレスは既に登録されています。';
        } else if (firebaseError.code === 'functions/invalid-argument') {
          errorMessage = '入力内容が正しくありません。';
        } else if (firebaseError.message) {
          errorMessage = firebaseError.message;
        }
      }

      setErrorText(errorMessage);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (user === null) {
      return;
    }
    if (!isOperatorStaff(user.role)) {
      navigate('/home');
    }
  }, [user, isLoading, navigate]);

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          },
        ]}
      >
        <Box sx={styles.formCard}>
          <Typography sx={styles.title}>企業招待</Typography>
          <Controller
            render={({ field }): JSX.Element => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.companyName)}
                formMb="24px"
                helperText={errors.companyName?.message}
                labelText="会社名"
                placeholder="株式会社〇〇"
                requiredText="必須"
                width="100%"
              />
            )}
            control={control}
            name="companyName"
          />

          <Controller
            render={({ field }): JSX.Element => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.userName)}
                formMb="24px"
                helperText={errors.userName?.message}
                labelText="担当者名"
                placeholder="山田 太郎"
                requiredText="必須"
                width="100%"
              />
            )}
            control={control}
            name="userName"
          />

          <Controller
            render={({ field }): JSX.Element => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.email)}
                formMb="24px"
                helperText={errors.email?.message}
                labelText="メールアドレス"
                placeholder={EMAIL_INPUT_PLACEHOLDER}
                requiredText="必須"
                width="100%"
              />
            )}
            control={control}
            name="email"
          />

          <Button
            disabled={buttonDisabledFlag()}
            onClick={async (): Promise<void> => {
              setFirstSubmit(true);
              await handleSubmit(async () => {
                onSubmit();
              })();
            }}
            sx={[primaryButtonBackgroundColor, styles.button]}
            variant="contained"
          >
            招待メールを送る
          </Button>
        </Box>
        <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
      </Box>
    </Box>
  );
};

export default CompanyInvite;
